import { z } from 'zod';

import { requiredString } from './primitives';

/* =====================================================================
   Schemas de jugador.
   - registerFormSchema: lo que envía el form de registro (guía v2 § 6.4)
   - playerSchema:       lo que se persiste en RTDB en /players/{id}
   - playersMapSchema:   { id: Player } para leaderboard
===================================================================== */

export const positionSchema = z.enum(['C-Level / Director', 'Manager', 'Tech', 'Other']);
export type Position = z.infer<typeof positionSchema>;

export const scoresSchema = z.object({
  g1: z.number().nonnegative(),
  g2: z.number().nonnegative(),
  g3: z.number().nonnegative(),
  g4: z.number().nonnegative(),
  total: z.number().nonnegative(),
});
export type Scores = z.infer<typeof scoresSchema>;

export const gamesPlayedSchema = z.object({
  g1: z.boolean(),
  g2: z.boolean(),
  g3: z.boolean(),
  g4: z.boolean(),
});
export type GamesPlayed = z.infer<typeof gamesPlayedSchema>;

export const registerFormSchema = z.object({
  name: requiredString('El nombre').max(80, 'Máximo 80 caracteres'),
  email: requiredString('El email').email('Email inválido'),
  company: requiredString('La empresa').max(120, 'Máximo 120 caracteres'),
  phone: requiredString('El teléfono').regex(/^\d{10,12}$/, 'Sólo números, 10-12 dígitos'),
  position: positionSchema,
});
export type RegisterFormFields = z.infer<typeof registerFormSchema>;

/* Form parcial — name/email vienen de Google, sólo el usuario completa estos campos. */
export const completeProfileSchema = z.object({
  company: requiredString('La empresa').max(120, 'Máximo 120 caracteres'),
  phone: requiredString('El teléfono').regex(/^\d{10,12}$/, 'Sólo números, 10-12 dígitos'),
  position: positionSchema,
  companyEmail: requiredString('El correo empresarial').email('Correo inválido'),
});
export type CompleteProfileFields = z.infer<typeof completeProfileSchema>;

export const playerSchema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  // companyEmail es opcional para retrocompatibilidad con registros previos
  companyEmail: z.string().email().optional(),
  company: z.string().min(1),
  phone: z.string().min(1),
  position: z.string().min(1),
  scores: scoresSchema,
  gPlayed: gamesPlayedSchema,
  ts: z.number(),
  ownerUid: z.string().min(1),
});
export type Player = z.infer<typeof playerSchema>;

export const playersMapSchema = z.record(z.string(), playerSchema).nullable().transform((v) => v ?? {});
export type PlayersMap = z.infer<typeof playersMapSchema>;

export const companiesMapSchema = z.record(z.string(), z.string().min(1)).nullable().transform((v) => v ?? {});
export type CompaniesMap = z.infer<typeof companiesMapSchema>;
